import { Button, Modal, message } from "antd";
import React, { FC, useEffect, useMemo, useState } from "react";
import {
  RotateLeftOutlined,
  RotateRightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";

type DocumentUploadPreviewProps = {
  uploadFileModal: boolean;
  setUploadFileModal: CallableFunction;
  selectedFile: any;
  isLoading: boolean;
  handleUpload: any;
};

const DocumentUploadPreview: FC<DocumentUploadPreviewProps> = ({
  uploadFileModal,
  setUploadFileModal,
  selectedFile,
  isLoading,
  handleUpload
}) => {
  const [fileError, setFileError] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [rotationAngle, setRotationAngle] = useState(0);

  useEffect(() => {
    setZoomLevel(1);
    setRotationAngle(0);
  }, [selectedFile]);

  const objectURL = useMemo(() => {
    if (selectedFile?.file?.originFileObj instanceof Blob) {
      const file = selectedFile.file.originFileObj;
      const fileType = file.type;

      if (fileType === "application/pdf" || fileType.startsWith("image/")) {
        setFileError(null);
        return URL.createObjectURL(file);
      }
      setFileError("Only PDF and Image files are allowed.");
    }
    return null;
  }, [selectedFile]);

  const isImage = selectedFile?.file?.originFileObj?.type?.startsWith("image/");
  const isPdf = selectedFile?.file?.originFileObj?.type === "application/pdf";

  const isMobile = window.innerWidth <= 768;

  const handleSave = async () => {
    if (fileError) {
      message.error(fileError);
      return;
    }

    await handleUpload();
  };

  return (
    <>
      {/* ================= Scoped CSS ================= */}
      <style>
        {`
        .custom-upload-modal .ant-modal {
          max-width: 96vw !important;
        }

        .custom-upload-modal .ant-modal-content {
          padding: 0 !important;
        }

        .custom-upload-modal .ant-modal-body {
          padding: 8px !important;
        }

        .preview-container {
          width: 100%;
          height: 70vh;
          background: #fafafa;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .preview-container img {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
          transition: transform 0.3s ease;
        }

        .preview-container iframe {
          width: 100%;
          height: 100%;
          border: none;
        }

        .pdf-fallback {
          text-align: center;
          padding: 24px;
        }

        .pdf-fallback button {
          margin-top: 12px;
        }

        .zoom-controls {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }

        @media (max-width: 768px) {
          .preview-container {
            height: 60vh;
          }
        }
        `}
      </style>

      <Modal
        centered
        open={uploadFileModal}
        onCancel={() => setUploadFileModal(false)}
        width="100%"
        style={{ maxWidth: "95vw" }}
        zIndex={100000}
        className="custom-upload-modal"
        title={`Upload ${selectedFile?.file?.name || ""}`}
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: isImage ? "space-between" : "flex-end",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            {isImage && (
              <div className="zoom-controls">
                <Button
                  onClick={() => setZoomLevel((z) => Math.max(z - 0.1, 0.5))}
                >
                  <ZoomOutOutlined />
                </Button>
                <Button
                  onClick={() => setZoomLevel((z) => Math.min(z + 0.1, 3))}
                >
                  <ZoomInOutlined />
                </Button>
                <Button onClick={() => setRotationAngle((r) => r - 90)}>
                  <RotateLeftOutlined />
                </Button>
                <Button onClick={() => setRotationAngle((r) => r + 90)}>
                  <RotateRightOutlined />
                </Button>
              </div>
            )}

            <Button
              type="primary"
              loading={isLoading}
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
        }
      >
        {fileError && <div style={{ color: "red" }}>{fileError}</div>}

        {objectURL && (
          <div className="preview-container">
            {isImage && (
              <img
                src={objectURL}
                alt="preview"
                style={{
                  transform: `scale(${zoomLevel}) rotate(${rotationAngle}deg)`,
                }}
              />
            )}

            {isPdf && !isMobile && (
              <iframe src={objectURL} title="PDF Preview" />
            )}

            {isPdf && isMobile && (
              <div className="pdf-fallback">
                <FilePdfOutlined style={{ fontSize: 40, color: "#d32f2f" }} />
                <p>PDF preview is not supported on mobile.</p>
                <Button
                  type="primary"
                  onClick={() => window.open(objectURL!, "_blank")}
                >
                  Open PDF
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default DocumentUploadPreview;
